import { locationsApi } from '../api/locationsApi';
import { Location, LocationFormData, LocationFilterParams } from '../types/location.types';

export class LocationService {
  /**
   * Get all locations with optional filters
   */
  static async getLocations(params?: LocationFilterParams) {
    try {
      return await locationsApi.getAll(params);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      throw error;
    }
  }

  /**
   * Get a single location by ID
   */
  static async getLocation(id: string) {
    try {
      return await locationsApi.getById(id);
    } catch (error) {
      console.error(`Failed to fetch location with ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new location
   */
  static async createLocation(data: LocationFormData) {
    try {
      return await locationsApi.create(data);
    } catch (error) {
      console.error('Failed to create location:', error);
      throw error;
    }
  }

  /**
   * Update an existing location
   */
  static async updateLocation(id: string, data: Partial<LocationFormData>) {
    try {
      // Remove unwanted fields before sending to backend
      let filteredData = LocationService.formatForForm(data);
      // Remove any property not in LocationFormData
      const allowedKeys = [
        'code','name','locationType','locationSubType','description','parentId','facilityId','capacity','floorPlanUrl','barcode','qrCode','isActive','status'
      ];
      filteredData = Object.keys(filteredData)
        .filter(key => allowedKeys.includes(key))
        .reduce((obj, key) => {
          (obj as Record<string, any>)[key] = (filteredData as Record<string, any>)[key];
          return obj;
        }, {} as Partial<LocationFormData>);
      // Remove floorPlanUrl if not a valid URL or empty string
      if (
        'floorPlanUrl' in filteredData &&
        (typeof filteredData.floorPlanUrl !== 'string' || !filteredData.floorPlanUrl.trim() || !/^https?:\/\//.test(filteredData.floorPlanUrl))
      ) {
        delete (filteredData as any).floorPlanUrl;
      }
      // Debug: log the final payload
      console.log('Sending location update payload:', filteredData);
      return await locationsApi.update(id, filteredData);
    } catch (error) {
      console.error(`Failed to update location with ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a location
   */
  static async deleteLocation(id: string) {
    try {
      await locationsApi.delete(id);
    } catch (error) {
      console.error(`Failed to delete location with ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get locations by facility
   */
  static async getLocationsByFacility(facilityId: string) {
    try {
      return await locationsApi.getByFacility(facilityId);
    } catch (error) {
      console.error(`Failed to fetch locations for facility ${facilityId}:`, error);
      throw error;
    }
  }

  /**
   * Get child locations for a parent location
   */
  static async getChildLocations(parentId: string) {
    try {
      return await locationsApi.getChildren(parentId);
    } catch (error) {
      console.error(`Failed to fetch child locations for parent ${parentId}:`, error);
      throw error;
    }
  }

  /**
   * Format location for form
   */
  static formatForForm(location: Partial<Location>): Partial<LocationFormData> {
    const { 
      id, 
      createdAt, 
      updatedAt, 
      childrenCount, 
      facility,  // Remove the full facility object
      parent,    // Remove the full parent object
      ...formData 
    } = location;
    
    return formData;
  }
}
